import { useMemo, useState } from "react";
import type { FulfillmentOrderLine } from "../../types";
import { changedEntryFields, type EntryExistingDraft, type EntryParseResult, type EntryPreviewItem } from "../../service/confirmationEditorEntry";
import { calculateFulfillmentLineAmount, confirmationTravelerRoleLabel } from "../../service/fulfillmentOrderEditorService";
import { confirmationBillingCountUnit } from "../../service/confirmationEditorView";
import { formatConfirmationAmount as amountText, formatConfirmationDecimal as compact } from "../../service/confirmationDecimalDisplay";
import type { ConfirmationEntryProps } from "./types";
export type ConfirmationNewPreview = EntryPreviewItem & {
    previewKey: string;
};
export { confirmationTravelerRoleLabel } from "../../service/fulfillmentOrderEditorService";
/** 只压缩展示中无意义的单次因子，原量价与历史金额仍完整保留。 */
function costFormula(line: Partial<FulfillmentOrderLine>): string {
    const period = confirmationBillingCountUnit(line);
    if (line.unit === "项" && line.pricingUnitType === "unit" && Number(line.quantity) === 1 && Number(line.billingCount) === 1)
        return "整项金额";
    const count = period !== "次" || Number(line.billingCount) !== 1 ? ` × ${compact(line.billingCount)}${period}` : "";
    return `${compact(line.quantity)}${line.unit || ""}${count} × ${amountText(compact(line.unitPriceAmount))}`;
}
/** 新旧费用共用分类列，身份与修改方式独立保留。 */
export function miscEntryColumns(): string[] {
    return ["选择", "费用项目", "计算依据", "金额", "服务主体", "结算主体", "操作"];
}
/** 连续展示杂费，已有行以原 ID 回写，新行保留原文定位。 */
export function ConfirmationMiscEntryRows({ rows, newRows, issues, groupId, active, disabled, selectedNew, onSelectNew, selectedExisting, onSelectExisting, onLocate, sourceText, original, locations = {} }: {
    rows: EntryExistingDraft[];
    newRows: ConfirmationNewPreview[];
    issues: EntryParseResult["errors"];
    groupId: string;
    groupLabel?: string;
    rangeLabel?: string;
    active: boolean;
    onFocusGroup?: () => void;
    onChange: (rows: EntryExistingDraft[]) => void;
    runtime: ConfirmationEntryProps["runtime"];
    disabled: boolean;
    selectedNew: ReadonlySet<string>;
    onSelectNew: (key: string, selected: boolean) => void;
    selectedExisting?: ReadonlySet<string>;
    onSelectExisting?: (ids: string[], selected: boolean) => void;
    mealDays?: ReadonlyArray<{
        dayNo: number;
        date: string | null;
    }>;
    onMoveMeal?: (itemKey: string, targetDay: number) => void;
    onDragMeal?: (itemKey: string | null) => void;
    onLocate: (groupId: string, start: number, end: number, issue?: EntryParseResult["errors"][number]) => void;
    hideGroupHeading?: boolean;
    sourceText?: string;
    original?: boolean;
    locations?: Record<string, { sourceStart: number; sourceEnd: number }>;
}) {
    const [localSelected, setLocalSelected] = useState<Set<string>>(new Set());
    const sourceSelected = selectedExisting ?? localSelected;
    const selected = useMemo(() => new Set(rows.filter((row) => !row.removed && sourceSelected.has(row.base.id)).map((row) => row.base.id)), [rows, sourceSelected]);
    const selectExisting = (ids: string[], checked: boolean) => {
        if (disabled || original)
            return;
        if (onSelectExisting) {
            onSelectExisting(ids, checked);
            return;
        }
        setLocalSelected((previous) => {
            const next = new Set(previous);
            for (const id of ids) {
                if (checked)
                    next.add(id);
                else
                    next.delete(id);
            }
            return next;
        });
    };
    const columnCount = miscEntryColumns().length;
    const records: Array<{
        row?: EntryExistingDraft;
        addition?: ConfirmationNewPreview;
        line: Partial<FulfillmentOrderLine>;
        amount: string;
    }> = rows.map((row) => {
        const changed = changedEntryFields(row);
        let amount = row.base.amount;
        if (["quantity", "billingCount", "unitPriceAmount"].some((name) => name in changed)) {
            const normalized = { ...row.base, ...changed };
            try {
                amount = calculateFulfillmentLineAmount(normalized.quantity, normalized.billingCount, normalized.unitPriceAmount);
            }
            catch {
                amount = "";
            }
        }
        return { row, line: { ...row.base, ...row.patch }, amount };
    });
    records.push(...newRows.map((addition) => ({ addition, line: addition.patch, amount: addition.patch.amount || "0" })));
    return <tbody className="confirmation-entry-group" data-entry-group={groupId} data-active={active || undefined}>
    {selected.size && !original ? <tr><td colSpan={columnCount}><div className="confirmation-entry-bulk"><span>已选 {selected.size} 行</span>
    </div></td></tr> : null}
    {records.map(({ row, line, amount, addition }) => {
            const itemTitle = line.itemNameSnapshot;
            const source = addition || (original && sourceText !== undefined ? { sourceStart: 0, sourceEnd: sourceText.length } : row ? locations[row.base.id] : null);
            const locateSource = () => { if (source) onLocate(groupId, source.sourceStart, source.sourceEnd); };
            const title = source ? <button type="button" className="confirmation-entry-source" onClick={locateSource}>{itemTitle}</button> : <strong>{itemTitle}</strong>;
            const formula = <>{costFormula(line)} = <strong>{amountText(amount)}</strong></>;
            const lineNumber = source && sourceText !== undefined ? sourceText.slice(0, source.sourceStart).split("\n").length : row?.base.lineNo;
            return <tr key={row?.base.id || addition!.previewKey} className="confirmation-entry-detail confirmation-entry-cost-row" data-removed={row?.removed || undefined} data-preview-start={source?.sourceStart} data-preview-end={source?.sourceEnd} data-preview-group={source ? groupId : undefined} data-preview-key={addition?.previewKey}><td colSpan={columnCount}>
          <div className="confirmation-entry-cost-meta"><input type="checkbox" aria-label={row ? `编辑选择 ${row.base.id}` : `选择新增 ${addition!.title} ${addition!.key}`} checked={original || (row ? selected.has(row.base.id) : selectedNew.has(addition!.previewKey))} disabled={disabled || original || row?.removed} onChange={(event) => row ? selectExisting([row.base.id], event.target.checked) : onSelectNew(addition!.previewKey, event.target.checked)}/>{title}<span>{confirmationTravelerRoleLabel(line.travelerRole) || (line.unit === "项" ? "整项金额" : "指定数量")}</span><small>{lineNumber ? `第${lineNumber}行` : "新增费用"}</small></div>
          {source ? <button type="button" className="confirmation-entry-cost-formula confirmation-misc-formula" aria-label={`${itemTitle} 计算依据`} onClick={locateSource}>{formula}</button> : <div className="confirmation-entry-cost-formula confirmation-misc-formula" aria-label={`${itemTitle} 计算依据`}>{formula}</div>}
          <div className="confirmation-entry-cost-subjects"><span>服务：{line.fulfillmentSupplierName || "未设置 · 选填"}</span><span>结算：{line.settlementSupplierName || "待设置"}</span></div>
          {line.remark ? <p className="confirmation-entry-cost-remark">{line.remark}</p> : null}
        </td></tr>;
        })}
    {issues.map((issue) => <tr key={`${issue.sourceStart}-${issue.message}`} className="confirmation-entry-issue" data-preview-start={issue.sourceStart} data-preview-end={issue.sourceEnd} data-preview-group={groupId}><td colSpan={columnCount}><button type="button" onClick={() => onLocate(groupId, issue.sourceStart, issue.sourceEnd, issue)}><strong>{issue.message}</strong><span>{issue.raw}</span></button></td></tr>)}
    {!records.length && !issues.length ? <tr><td colSpan={columnCount} className="confirmation-entry-empty">在左侧填写费用，预览将在此展开</td></tr> : null}
    {rows.some((row) => row.removed) ? <tr><td colSpan={columnCount} className="confirmation-entry-warning">应用时仅移除明确标记的费用。</td></tr> : null}
  </tbody>;
}
